import React, { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { MoonIcon } from '../../icons';
import { MenuIcon } from '../uiIcons';
import Sheet from '../Sheet/Sheet';
import { useDesktopLayout } from '../useMediaQuery';
import './Shell.css';

export type ShellMode = 'parent' | 'donor';

export interface ShellNavItem {
  key?: string;
  label: string;
  to: string;
  icon: ReactNode;
  /** Match only the exact path (react-router NavLink end). */
  end?: boolean;
}

export interface ShellProps {
  mode: ShellMode;
  navItems?: ShellNavItem[];
  utilities?: ReactNode;
  /** Short environment note rendered inside the single header, never as a second bar. */
  notice?: ReactNode;
  player?: ReactNode;
  children: ReactNode;
  /** Where the brand wordmark links; omit for a static wordmark. */
  brandTo?: string;
  mainId?: string;
  className?: string;
}

const MENU_SHEET_ID = 'luma-shell-menu';

/**
 * Customer shell. Parent mode: fixed 240px rail from 1024px, a 100px utility
 * header on desktop, and a 72px header with a drawer below. Donor mode: a
 * 72px brand header at every width and no navigation. The skip link is the
 * first focus stop; the mounted player's height sets --luma-player-offset.
 */
const Shell: React.FC<ShellProps> = ({ mode, navItems = [], utilities, notice, player, children, brandTo, mainId = 'main-content', className }) => {
  const desktop = useDesktopLayout();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const [playerOffset, setPlayerOffset] = useState(0);
  const hasPlayer = Boolean(player);
  const showRail = mode === 'parent' && desktop;
  const showDrawer = mode === 'parent' && !desktop && navItems.length > 0;

  useEffect(() => {
    const node = playerRef.current;
    if (!hasPlayer || !node) {
      setPlayerOffset(0);
      return undefined;
    }
    const measure = () => setPlayerOffset(node.getBoundingClientRect().height || node.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasPlayer]);

  // The document scrolls, not main. Give keyboard/anchor scrolling the same
  // clearance as the content; restore its previous value when this shell exits.
  useLayoutEffect(() => {
    if (!hasPlayer || !shellRef.current) return;
    const root = document.documentElement;
    const previous = root.style.scrollPaddingBottom;
    const gap = getComputedStyle(shellRef.current).getPropertyValue('--luma-page-end-gap').trim() || '0px';
    root.style.scrollPaddingBottom = `calc(${gap} + ${playerOffset}px)`;
    return () => { root.style.scrollPaddingBottom = previous; };
  }, [hasPlayer, playerOffset]);

  useEffect(() => {
    if (desktop) setMenuOpen(false);
  }, [desktop]);

  const brand = (
    <span className="luma-shell__wordmark">
      <MoonIcon size={22} />
      <span>Luma</span>
    </span>
  );
  const brandNode = brandTo ? (
    <Link className="luma-shell__brand" to={brandTo} aria-label="Luma home">
      {brand}
    </Link>
  ) : (
    <span className="luma-shell__brand">{brand}</span>
  );

  const navTree = (onNavigate?: () => void) => (
    <nav className="luma-shell__nav" aria-label="Main">
      <ul className="luma-shell__nav-list">
        {navItems.map((item) => (
          <li key={item.key ?? item.to} className="luma-shell__nav-item">
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) => `luma-shell__nav-link${isActive ? ' luma-shell__nav-link--active' : ''}`}
              onClick={onNavigate}
            >
              <span className="luma-shell__nav-circle" aria-hidden="true">{item.icon}</span>
              <span className="luma-shell__nav-label">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );

  const style = { '--luma-player-offset': `${playerOffset}px` } as CSSProperties;
  const classes = [
    'luma-shell',
    `luma-shell--${mode}`,
    showRail ? 'luma-shell--rail' : 'luma-shell--compact',
    hasPlayer ? 'luma-shell--has-player' : null,
    className || null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={shellRef} className={classes} style={style} data-mode={mode} data-layout={showRail ? 'desktop' : 'compact'}>
      <a className="luma-shell__skip" href={`#${mainId}`}>Skip to main content</a>

      {showRail && (
        <aside className="luma-shell__rail">
          <div className="luma-shell__rail-brand">{brandNode}</div>
          {navItems.length > 0 && navTree()}
        </aside>
      )}

      <div className="luma-shell__column">
        <header className={`luma-shell__header${showRail ? ' luma-shell__header--desktop' : ' luma-shell__header--compact'}`}>
          {!showRail && <div className="luma-shell__header-brand">{brandNode}</div>}
          {notice && <div className="luma-shell__notice" role="note">{notice}</div>}
          <div className="luma-shell__utilities">
            {utilities}
            {showDrawer && (
              <button
                ref={menuButtonRef}
                type="button"
                className="luma-shell__menu-button"
                aria-label="Menu"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                aria-controls={MENU_SHEET_ID}
                onClick={() => setMenuOpen(true)}
              >
                <MenuIcon size={24} />
              </button>
            )}
          </div>
        </header>

        <main id={mainId} className="luma-shell__main" tabIndex={-1}>
          {children}
        </main>
      </div>

      {showDrawer && (
        <Sheet id={MENU_SHEET_ID} open={menuOpen} title="Menu" onClose={() => setMenuOpen(false)} returnFocusRef={menuButtonRef}>
          {navTree(() => setMenuOpen(false))}
        </Sheet>
      )}

      {hasPlayer && (
        <div ref={playerRef} className="luma-shell__player" data-testid="luma-shell-player">
          {player}
        </div>
      )}
    </div>
  );
};

export default Shell;
